import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import CustomHandle from 'features/co-pilot/campaign-builder/campaign-flow/custom-handle';
import { FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { memo, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Edge, getConnectedEdges, NodeProps, Position, useReactFlow, useStore } from 'reactflow';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import useTopNodeMenuStore from 'stores/top-node-menu-store';
import { makeStyles } from 'tss-react/mui';
import { findConnectedNodes } from 'utils/flowNode.utils';
import { v4 as uuidv4 } from 'uuid';
import { CampaignBuilderSteps } from '../campaign-builder.types';
import usePromptLibrary from '../use-prompt-library';
import ConfirmDeleteNodeDialog from './confirm-delete-node-dialog';

const selector = (s: any) => ({
  nodeInternals: s.nodeInternals,
  edges: s.edges,
});

const MessageNode = ({ id, data, xPos, yPos }: NodeProps) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const topBtnRef = useRef(null);
  const bottomBtnRef = useRef(null);

  const { setNodes, setEdges, getNode } = useReactFlow();
  const { nodeInternals, edges } = useStore(selector);

  const handleOpenBottomMenu = useBottomNodeMenuStore((state) => state.handleOpenMenu);
  const handleOpenTopMenu = useTopNodeMenuStore((state) => state.handleOpenMenu);

  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
    setSelectedNode: state.setSelectedNode,
  }));

  const { campaignFunctions } = usePromptLibrary();
  const { functionName, functionNumber } = data;
  const position = { x: xPos, y: yPos };
  const hasSourceEdge = !!edges.find((edge: Edge) => edge.source === id);
  const hasTargetEdge = !!edges.find((edge: Edge) => edge.target === id);

  const selected = selectedNode?.id === id;

  const [confirmDeleteNodeDialog, setConfirmDeleteNodeDialog] = useState<{ open: boolean }>({ open: false });

  const updatedFunctions = useMemo(() => {
    const functionsCopy = [...campaignFunctions];

    const alreadyHasCallTranscript = functionsCopy.some((cf) => cf.functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT);

    if (!alreadyHasCallTranscript) {
      functionsCopy.push({
        functionName: FunnelFunctions.SEND_CALL_TRANSCRIPT,
        prompt: t('coldCallTranscript'),
      });
    }

    return functionsCopy;
  }, [campaignFunctions]);

  const options = useMemo(() => {
    return updatedFunctions.map((cf) => ({
      label: cf.functionName,
      value: cf.functionName,
    }));
  }, [updatedFunctions]);

  const getHeight = () => {
    const currNode = getNode(id);
    if (!_.isNil(currNode) && !_.isNil(currNode.height)) return currNode.height;
    return undefined;
  };

  /**
   * Node deletion handlers
   */

  const handleOpenConfirmDeleteNodeDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setConfirmDeleteNodeDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseConfirmDeleteNodeDialog = () => {
    setConfirmDeleteNodeDialog({ open: false });
  };

  const handleDeleteNode = () => {
    const nodeToDelete = nodeInternals.get(id);
    if (!nodeToDelete) return;

    const nodeHeight = (getHeight() || 225) + 84;
    const connectedEdges = getConnectedEdges([nodeToDelete], edges);

    // If exactly 2 edges, we can link them so flow doesn't break:
    if (connectedEdges.length === 2 && connectedEdges[0].target !== connectedEdges[1].target) {
      const targetEdge = connectedEdges.find((edge) => edge.source === id);
      const sourceEdge = connectedEdges.find((edge) => edge.target === id);

      if (targetEdge && sourceEdge) {
        const updatedEdge = {
          ...sourceEdge,
          target: targetEdge.target,
        };

        setEdges((currEdges) => currEdges.filter((e) => e.id !== targetEdge.id && e.id !== sourceEdge.id).concat(updatedEdge));
      }
    } else {
      // Otherwise, remove all edges connected to this node
      const connectedEdgeIds = connectedEdges.map((e) => e.id);
      setEdges((currEdges) => currEdges.filter((e) => !connectedEdgeIds.includes(e.id)));
    }

    // If this node was selected, clear the selection
    if (selectedNode?.id === id) {
      setSelectedNode(null);
    }

    // Remove the node itself and re-position downstream nodes
    setNodes((currentNodes) => {
      const downstreamNodes = findConnectedNodes(id, edges).filter((nodeId) => nodeId !== id);
      return currentNodes
        .filter((node) => node.id !== id)
        .map((node) => {
          if (downstreamNodes.includes(node.id)) {
            return {
              ...node,
              position: {
                ...node.position,
                y: node.position.y - nodeHeight,
              },
            };
          }
          return node;
        });
    });
  };

  const handleFunctionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFunctionName = event.target.value;

    setNodes((nodes) => {
      const updatedNodes = _.cloneDeep(nodes);
      const foundNode = updatedNodes.find((node) => node.id === id);

      if (foundNode) {
        foundNode.data.functionName = selectedFunctionName;

        const newFunc = updatedFunctions.find((cf) => cf.functionName === selectedFunctionName);
        if (newFunc) {
          foundNode.data.prompt = newFunc.prompt;
        }

        setSelectedNode(foundNode);

        const connectedEdges = getConnectedEdges([foundNode], edges);
        const hasOutgoingEdge = connectedEdges.some((edge) => edge.source === id);

        if (selectedFunctionName === FunnelFunctions.SEND_CONNECTION_REQUEST && !hasOutgoingEdge) {
          const newNodeId = uuidv4();
          const newNode = {
            id: newNodeId,
            type: NodeVariants.CONDITION_WITH_DURATION_NODE,
            position: {
              x: foundNode.position.x + 0,
              y: foundNode.position.y + 225,
            },
            deletable: false,
            selectable: false,
            data: {
              isStartNode: false,
              functionName: FunnelFunctions.CHECK_IF_CONNECTED_UNTIL,
              waitTillNext: 2 * 60 * 60,
            },
          };

          updatedNodes.push(newNode);

          setEdges((existingEdges) => [
            ...existingEdges,
            {
              id: uuidv4(),
              source: foundNode.id,
              target: newNodeId,
              type: 'buttonedge',
              deletable: false,
              style: { stroke: theme.palette.greys.medium },
            },
          ]);
        }
      }

      return updatedNodes;
    });
  };

  /**
   * Node selection handler
   */

  const handleSelectNode = () => {
    setSelectedNode(getNode(id) || null);

    const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
    const stepIndex = searchParams.get('stepIndex');

    if (stepIndex === `${campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_TESTING)}`) {
      setSearchParams(
        (prev) => {
          prev.set('stepIndex', `${campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW)}`);
          return prev;
        },
        { replace: true }
      );
    }
  };

  return (
    <>
      {/* If not a start node, show a handle on top */}
      <CustomHandle className={cx(classes.basicHandle, 'top')} type="target" position={Position.Top} isConnectable={1}>
        {!hasTargetEdge && (
          <PaiIconButton
            ref={topBtnRef}
            sx={{
              position: 'absolute',
              left: 'calc((40px - 16px + 2px) / -2)',
              top: '-56px',
            }}
            onClick={() => handleOpenTopMenu(topBtnRef.current, id, position, getHeight())()}
          >
            <AddRoundedIcon />
          </PaiIconButton>
        )}
      </CustomHandle>

      <Stack className={cx(classes.node, selected ? 'selected' : undefined)}>
        {/* The close/delete icon */}
        <IconButton className={cx(classes.closeBtn, 'nodrag', 'nopan')} size="small" onClick={handleOpenConfirmDeleteNodeDialog}>
          <CloseRoundedIcon className={classes.closeIcon} />
        </IconButton>

        {/* Node Header */}
        <Stack
          className={cx(classes.nodeHead, selected ? 'selected' : undefined)}
          direction="row"
          alignItems="center"
          gap={1.5}
          onClick={handleSelectNode}
        >
          <span className={classes.nodeNumber}>{functionNumber ?? '—'}</span>
          <Typography className={classes.nodeHeadText}>{t('message')}</Typography>
        </Stack>

        {/* Main content: the function select */}
        <Stack className={cx(classes.nodeContent, 'nodrag', 'nopan')} gap={2}>
          {!_.isEmpty(campaignFunctions) ? (
            <PaiSelect value={functionName || ''} variant="background" onChange={handleFunctionNameChange}>
              {options.map((option, index) => (
                <PaiMenuItem key={index} value={option.value}>
                  {t(option.label)}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          ) : (
            <Typography className={classes.fallbackFunctionName}>
              {functionName ? t(functionName) : t('noActionSelected')}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* The bottom handle */}
      <CustomHandle className={cx(classes.basicHandle, 'bottom')} type="source" position={Position.Bottom} isConnectable={1}>
        {!hasSourceEdge && (
          <PaiIconButton
            ref={bottomBtnRef}
            sx={{
              position: 'absolute',
              left: 'calc((40px - 16px + 2px) / -2)',
              bottom: '-56px',
            }}
            onClick={() => handleOpenBottomMenu(bottomBtnRef.current, id, position, getHeight())()}
          >
            <AddRoundedIcon />
          </PaiIconButton>
        )}
      </CustomHandle>

      <ConfirmDeleteNodeDialog
        open={confirmDeleteNodeDialog.open}
        handleClose={handleCloseConfirmDeleteNodeDialog}
        handleDeleteNode={handleDeleteNode}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  node: {
    position: 'relative',
    background: '#FFF',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '10px',
    width: '320px',
    minHeight: '100px',
    '&.selected': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  basicHandle: {
    background: `${theme.palette.greys.medium}`,
    borderRadius: '4px',
    zIndex: 99,
    width: '16px',
    height: '16px',
    '&.top': {
      top: '-8px',
    },
    '&.bottom': {
      bottom: '-8px',
    },
  },
  nodeHead: {
    padding: '12px 16px',
    borderBottom: '1px solid #F0F0F5',
    cursor: 'pointer',
    '&:hover': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
    },
    '&.selected': {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
      '& span': {
        background: theme.palette.primary.main,
      },
    },
  },
  nodeNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: '#B0A9FF',
    color: '#FFF',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
  },
  nodeHeadText: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  closeBtn: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  nodeContent: {
    padding: '14px 16px',
  },
  fallbackFunctionName: {
    minHeight: theme.typography.pxToRem(40),
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.light}`,
    color: theme.palette.greys.medium,
    backgroundColor: '#FFF',
  },
}));

export default memo(MessageNode);
